import React from "react";
import { Breadcrumbs } from "web/react/components/breadcrumbs";
import type {
    FeatureArticleHeaderData,
    FeatureArticleHero,
} from "web/react/components/feature-article/types";
import { Heading } from "web/react/emo/heading";
import { ImageContainer } from "web/react/emo/image-container";
import { Text } from "web/react/emo/text";
import { VStack } from "web/react/emo/v-stack";
import { formatDateMDY } from "web/script/utils/dates";
import * as styles from "./feature-article-header.css";

interface HeaderProps {
    header: FeatureArticleHeaderData;
    hero: FeatureArticleHero;
    disclosure: string | null;
}

export function FeatureArticleHeader({
    header,
    hero,
    disclosure,
}: HeaderProps): React.ReactElement {
    const { title, subtitle, author, author_meta: authorMeta, display_date: displayDate } = header;

    const breadcrumbs = [
        { name: "Home", url: "/" },
        { name: "Feature articles", url: null },
        { name: title, url: null },
    ];

    const updatedAt = displayDate ? formatDateMDY(displayDate) : null;

    return (
        <div className={styles.wrapper}>
            <Breadcrumbs breadcrumbs={breadcrumbs} type="feature_article" />

            {!!disclosure && (
                <div className={styles.disclosure}>
                    <Text textStyle="caption-s" color="secondary" upperCase>
                        {disclosure}
                    </Text>
                </div>
            )}

            <VStack spacing="sm">
                <Heading as="h1" textStyle={{ sm: "hero", lg: "hero-xl" }}>
                    {title}
                </Heading>
                {!!subtitle && (
                    <Text as="p" textStyle={{ sm: "hero-xs", lg: "hero-s" }}>
                        {subtitle}
                    </Text>
                )}
            </VStack>

            <VStack spacing="xxs">
                {!!author && (
                    <Text textStyle="caption-s" color="secondary" upperCase>
                        {author}
                    </Text>
                )}
                {!!authorMeta && (
                    <Text textStyle="caption-s" color="secondary" upperCase>
                        {authorMeta}
                    </Text>
                )}
                {!!updatedAt && (
                    <Text textStyle="caption-s" color="secondary" upperCase>
                        {`Updated ${updatedAt}`}
                    </Text>
                )}
            </VStack>

            {hero.image_url && (
                <ImageContainer
                    imageSrc={hero.image_url}
                    imageCredit={hero.caption}
                    textStyle="caption-s"
                    alt={hero.caption ?? ""}
                />
            )}
        </div>
    );
}
